import { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb, { getCategoryName } from '@/components/feature/Breadcrumb';
import { categories, articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const category = categories.find((c) => c.slug === slug);
  if (!category) {
    return <Navigate to="/" replace />;
  }

  const filteredArticles = articles.filter((a) => a.category === slug);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 小宝探花`,
    description: category.description,
    url: `https://npjyf.cn/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '小宝探花',
      url: 'https://npjyf.cn',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 pb-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: category.name },
                ]}
              />
            </div>

            <div className="flex flex-col md:flex-row md:items-center gap-3 mb-8">
              <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className={`${category.icon} text-xl md:text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">{category.description}</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`whitespace-nowrap px-4 py-2 rounded-full text-sm transition-all ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50 font-medium'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group block rounded-lg overflow-hidden bg-background-100 hover:bg-background-200/70 transition-all duration-300"
                  >
                    <div className="relative w-full aspect-video overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-background-50/80 group-hover:bg-primary-500 transition-colors">
                          <i className="ri-play-fill text-xl text-foreground-900 group-hover:text-background-50 ml-0.5"></i>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 md:p-5">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {article.tags.slice(0, 2).map((tag) => (
                          <span key={tag} className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h3 className="text-sm md:text-base font-semibold font-heading text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-xs md:text-sm text-foreground-600 line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-foreground-500">
                        <span>{article.date}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-inbox-line text-3xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容，请查看其他分类</p>
              </div>
            )}
          </div>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}